import { useState } from "react";
import { toast } from "sonner";
import { Store } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { api, formatError } from "@/lib/api";
import { Field, DayChips, SuccessCard, FormCard, SubmitButton } from "./kit";

const EMPTY = { business_name: "", contact_name: "", email: "", phone: "", offerings: "", days: [] };

export const VendorSection = ({ content }) => {
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.days.length) return setError("Please select at least one day.");
    setLoading(true);
    try {
      await api.post("/vendors", f);
      setDone(true);
      toast.success("Vendor application sent");
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="vendor" className="scroll-mt-20 border-t border-neutral-900 px-5 py-16 md:py-20" data-testid="vendor-section">
      <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[0.8fr_1.2fr]">
        <div>
          <Store className="mb-4 h-8 w-8 text-amber-400" strokeWidth={1.5} />
          <h2 className="font-display text-3xl font-bold text-gold sm:text-4xl">
            Wanna be a <span className="font-script normal-case tracking-normal text-red-500">vendor?</span>
          </h2>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-neutral-400" data-testid="vendor-fee-note">
            Vendor fees are paid directly to the {content.contact_name || "Alagba"}, who will share pricing after reviewing your application.
          </p>
        </div>
        {done ? (
          <SuccessCard testid="vendor-success" title="Application received">
            Thank you! The {content.contact_name || "Alagba"} will review it and reach out with pricing and details.
          </SuccessCard>
        ) : (
          <FormCard>
            <form onSubmit={submit} className="space-y-4" data-testid="vendor-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Business name" id="v-biz" required value={f.business_name} onChange={set("business_name")} data-testid="vendor-business-input" />
                <Field label="Contact name" id="v-name" required value={f.contact_name} onChange={set("contact_name")} data-testid="vendor-contact-input" />
                <Field label="Email" id="v-email" type="email" required value={f.email} onChange={set("email")} data-testid="vendor-email-input" />
                <Field label="Phone" id="v-phone" type="tel" required value={f.phone} onChange={set("phone")} data-testid="vendor-phone-input" />
              </div>
              <Field label="What do you sell?" id="v-sells">
                <Textarea id="v-sells" required maxLength={2000} value={f.offerings} onChange={set("offerings")} className="min-h-[80px] border-neutral-800 bg-neutral-950 text-base" placeholder="Food, crafts, clothing…" data-testid="vendor-offerings-input" />
              </Field>
              <Field label="Days wanted">
                <DayChips value={f.days} onChange={set("days")} schedule={content.schedule} prefix="vendor" />
              </Field>
              {error && <p className="rounded-md border border-red-800 bg-red-950/50 p-3 text-sm text-red-300" data-testid="vendor-error">{error}</p>}
              <SubmitButton loading={loading} testid="vendor-submit-button">Apply to Vend</SubmitButton>
            </form>
          </FormCard>
        )}
      </div>
    </section>
  );
};
